import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Film, Plus, History, Clapperboard, MoreVertical,
  Send, ArrowDownToLine, ArrowUpFromLine, Zap,
  CornerUpLeft, Pencil, Power, RotateCcw,
  Key, TrendingDown, CheckCircle2, Building2,
} from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import { listMovies, listKeyHoldings } from "../../api/movieKeys";
import { listBranches } from "../../api/branches";
import { unwrapList, errorMessage, formatDate } from "../../lib/records";
import { Button } from "../../components/ui";
import {
  AddFilmModal,
  IssueModal,
  AllocateModal,
  ConsumeModal,
  ReturnModal,
  CloseFilmModal,
} from "./MovieKeyModals";

const STATUS_STYLE = {
  ACTIVE: { bg: "bg-emerald-100", text: "text-emerald-800", dot: "bg-emerald-500", label: "Active" },
  CLOSED: { bg: "bg-gray-100",    text: "text-gray-500",    dot: "bg-gray-400",    label: "Closed" },
};

const FILM_LIMIT = 10;

function StatCard({ icon: Icon, label, value, color, borderColor, bgColor, iconBg }) {
  return (
    <div className={`rounded-2xl border ${borderColor} ${bgColor} p-4 flex items-center gap-4`}>
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${iconBg}`}>
        <Icon size={20} className={color} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 font-medium truncate">{label}</p>
        <p className={`text-2xl font-bold tabular-nums mt-0.5 ${color}`}>{value}</p>
      </div>
    </div>
  );
}

function ActionMenu({ film, isChain, onAction }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function close(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  function act(action) { setOpen(false); onAction(action); }

  const isActive = film.status === "ACTIVE";
  const items = [];
  if (isChain && isActive)  items.push({ key: "issue",    label: "Issue keys",    icon: Send,            color: "text-blue-600",   bg: "hover:bg-blue-50" });
  if (isActive)             items.push({ key: "allocate", label: "Allocate keys", icon: ArrowDownToLine, color: "text-purple-600", bg: "hover:bg-purple-50" });
  if (isActive)             items.push({ key: "revoke",   label: "Revoke keys",   icon: ArrowUpFromLine, color: "text-red-500",    bg: "hover:bg-red-50" });
  if (isActive)             items.push({ key: "consume",  label: "Record show",   icon: Zap,             color: "text-amber-600",  bg: "hover:bg-amber-50" });
  if (!isChain && isActive) items.push({ key: "return",   label: "Return keys",   icon: CornerUpLeft,    color: "text-gray-600",   bg: "hover:bg-gray-50" });
  if (isChain) items.push({ key: "edit",  label: "Edit film",  icon: Pencil, color: "text-emerald-600", bg: "hover:bg-emerald-50" });
  if (isChain) items.push({
    key: "close",
    label: film.status === "CLOSED" ? "Reopen film" : "Close film",
    icon: film.status === "CLOSED" ? RotateCcw : Power,
    color: film.status === "CLOSED" ? "text-emerald-600" : "text-red-500",
    bg: film.status === "CLOSED" ? "hover:bg-emerald-50" : "hover:bg-red-50",
  });
  items.push({ key: "history", label: "Movement history", icon: History, color: "text-gray-500", bg: "hover:bg-gray-50" });

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
      >
        <MoreVertical size={16} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 w-52 rounded-xl bg-white border border-gray-100 shadow-xl py-1.5">
          {items.map(it => (
            <button
              key={it.key}
              onClick={() => act(it.key)}
              className={`flex w-full items-center gap-2.5 px-3.5 py-2 text-sm font-medium transition ${it.bg} ${
                it.key === "close" && film.status !== "CLOSED" ? "border-t border-gray-100 mt-1 pt-2.5" : ""
              }`}
            >
              <it.icon size={15} className={it.color} />
              <span className="text-gray-700">{it.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MovieKeysList() {
  const { partner } = useAuth();
  const isChain = partner?.kind === "CHAIN";
  const navigate = useNavigate();

  const [films, setFilms] = useState([]);
  const [filmPagination, setFilmPagination] = useState(null);
  const [filmPage, setFilmPage] = useState(1);
  const [loadingFilms, setLoadingFilms] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingStats, setLoadingStats] = useState(true);

  const [holdingsByMovie, setHoldingsByMovie] = useState({});
  const [globalStats, setGlobalStats] = useState({ available: 0, consumed: 0, issued: 0 });

  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");

  const sentinelRef = useRef(null);

  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [issueTarget, setIssueTarget] = useState(null);
  const [allocateTarget, setAllocateTarget] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [consumeTarget, setConsumeTarget] = useState(null);
  const [returnTarget, setReturnTarget] = useState(null);
  const [closeTarget, setCloseTarget] = useState(null);

  useEffect(() => {
    if (!isChain) return;
    listBranches({ limit: 100 })
      .then(res => setBranches(unwrapList(res).items))
      .catch(() => {});
  }, [isChain]);

  const loadHoldings = useCallback(async () => {
    setLoadingStats(true);
    try {
      const params = { page: 1, limit: 100 };
      if (branchFilter) params.partner = branchFilter;
      const firstH = await listKeyHoldings(params);
      const { items: firstItems, pagination: hPg } = unwrapList(firstH, 100);
      let all = [...firstItems];
      if ((hPg?.pages ?? 1) > 1) {
        const rest = await Promise.all(
          Array.from({ length: hPg.pages - 1 }, (_, i) =>
            listKeyHoldings({ ...params, page: i + 2 }).then(r => unwrapList(r, 100).items)
          )
        );
        all = all.concat(rest.flat());
      }
      const grouped = {};
      let totalAvailable = 0, totalConsumed = 0, totalIssued = 0;
      all.forEach(h => {
        totalAvailable += h.availableKeys || 0;
        totalConsumed += h.consumedKeys || 0;
        totalIssued += h.issuedKeys || 0;
        const mid = h.movie?._id ?? h.movie;
        if (!mid) return;
        if (!grouped[mid]) grouped[mid] = { available: 0, issued: 0, consumed: 0 };
        grouped[mid].available += h.availableKeys || 0;
        grouped[mid].issued += h.issuedKeys || 0;
        grouped[mid].consumed += h.consumedKeys || 0;
      });
      setHoldingsByMovie(grouped);
      setGlobalStats({ available: totalAvailable, consumed: totalConsumed, issued: totalIssued });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoadingStats(false);
    }
  }, [branchFilter]);

  const loadFilms = useCallback(async (p = 1, append = false) => {
    if (!append) setLoadingFilms(true); else setLoadingMore(true);
    try {
      const res = await listMovies({ page: p, limit: FILM_LIMIT });
      const { items, pagination: pg } = unwrapList(res, FILM_LIMIT);
      setFilms(prev => append ? [...prev, ...items] : items);
      setFilmPagination(pg);
      setFilmPage(p);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoadingFilms(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadFilms(1, false);
    loadHoldings();
  }, [loadFilms, loadHoldings]);

  // Infinite scroll
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (
        entry.isIntersecting &&
        filmPagination &&
        filmPage < (filmPagination.pages ?? 1) &&
        !loadingMore &&
        !loadingFilms
      ) {
        loadFilms(filmPage + 1, true);
      }
    }, { rootMargin: "150px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [filmPagination, filmPage, loadingMore, loadingFilms, loadFilms]);

  function done(clearFn) {
    clearFn();
    loadFilms(1, false);
    loadHoldings();
  }

  function handleAction(film, action) {
    switch (action) {
      case "issue":    setIssueTarget(film); break;
      case "allocate": setAllocateTarget(film); break;
      case "revoke":   setRevokeTarget(film); break;
      case "consume":  setConsumeTarget(film); break;
      case "return":   setReturnTarget(film); break;
      case "edit":     setEditTarget(film); break;
      case "close":    setCloseTarget(film); break;
      case "history":  navigate(`/cinema/history?movie=${film._id}`); break;
    }
  }

  const selectedBranchName = branches.find(b => b._id === branchFilter)?.name ?? "";

  return (
    <div className="space-y-5 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Clapperboard size={22} className="text-emerald-600" />
            Cinema · Movie Keys
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">DCP key issuance and show consumption</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="ghost"
            onClick={() => navigate("/cinema/history")}
            className="text-sm text-gray-600 border border-gray-200"
          >
            <History size={14} /> History
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/cinema/shows")}
            className="text-sm text-gray-600 border border-gray-200"
          >
            <Film size={14} /> Shows
          </Button>
          {isChain && (
            <Button
              onClick={() => setAddOpen(true)}
              className="bg-gradient-to-r from-emerald-500 to-green-600 text-white text-sm"
            >
              <Plus size={15} /> Add Film
            </Button>
          )}
        </div>
      </div>

      {/* Branch filter — CHAIN only */}
      {isChain && branches.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <Building2 size={14} className="text-gray-400 shrink-0" />
          <span className="text-xs text-gray-500 font-medium">Filter by branch:</span>
          <select
            value={branchFilter}
            onChange={e => setBranchFilter(e.target.value)}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 bg-white"
          >
            <option value="">All branches</option>
            {branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
          </select>
          {branchFilter && (
            <button
              onClick={() => setBranchFilter("")}
              className="text-xs text-gray-400 hover:text-gray-600 transition px-2 py-1 rounded hover:bg-gray-100"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* Summary stat cards */}
      {loadingStats ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[1, 2, 3].map(i => <div key={i} className="h-[76px] rounded-2xl bg-gray-100 animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <StatCard
            icon={Key}
            label={branchFilter ? `Available · ${selectedBranchName}` : isChain ? "Keys Available (all branches)" : "Keys Available"}
            value={globalStats.available}
            color="text-emerald-600"
            borderColor="border-emerald-200"
            bgColor="bg-emerald-50/70"
            iconBg="bg-emerald-100"
          />
          <StatCard
            icon={TrendingDown}
            label={branchFilter ? `Consumed · ${selectedBranchName}` : "Keys Consumed"}
            value={globalStats.consumed}
            color="text-amber-600"
            borderColor="border-amber-200"
            bgColor="bg-amber-50/70"
            iconBg="bg-amber-100"
          />
          <StatCard
            icon={CheckCircle2}
            label={branchFilter ? `Issued · ${selectedBranchName}` : isChain ? "Total Issued (from distributor)" : "Keys Received"}
            value={globalStats.issued}
            color="text-blue-600"
            borderColor="border-blue-200"
            bgColor="bg-blue-50/70"
            iconBg="bg-blue-100"
          />
        </div>
      )}

      {/* Film list */}
      {loadingFilms ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 rounded-xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      ) : films.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-200 p-12 text-center">
          <Film size={36} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-600 font-medium">No films yet</p>
          <p className="text-sm text-gray-400 mt-1">
            Films are added at the chain level and shared with all branches.
          </p>
          {isChain && (
            <Button onClick={() => setAddOpen(true)} className="mt-4 bg-emerald-500 text-white text-sm">
              Add your first film
            </Button>
          )}
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {films.map(film => {
              const stats = holdingsByMovie[film._id] ?? {};
              const style = STATUS_STYLE[film.status] ?? STATUS_STYLE.ACTIVE;
              return (
                <div
                  key={film._id}
                  className="rounded-xl bg-white border border-gray-100 shadow-sm p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-3">
                    <div className={`shrink-0 flex items-center justify-center h-10 w-10 rounded-lg ${
                      film.status === "ACTIVE" ? "bg-emerald-50" : "bg-gray-50"
                    }`}>
                      <Film size={18} className={film.status === "ACTIVE" ? "text-emerald-500" : "text-gray-400"} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{film.title}</span>
                        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium">
                          <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
                          <span className={style.text}>{style.label}</span>
                        </span>
                        {film.format && (
                          <span className="text-[11px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-semibold">
                            {film.format}
                          </span>
                        )}
                        {film.certification && (
                          <span className="text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-md font-bold border border-amber-200">
                            {film.certification}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-400 flex-wrap">
                        {film.language && <span>{film.language}</span>}
                        {film.distributor && <><span className="text-gray-300">·</span><span>{film.distributor}</span></>}
                        {film.releaseDate && <><span className="text-gray-300">·</span><span>{formatDate(film.releaseDate)}</span></>}
                      </div>
                    </div>

                    <ActionMenu film={film} isChain={isChain} onAction={action => handleAction(film, action)} />
                  </div>

                  <div className="flex items-center gap-5 mt-3 pt-3 border-t border-gray-50">
                    <div className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-emerald-400" />
                      <span className="text-xs text-gray-500">Available</span>
                      <span className="text-sm font-bold tabular-nums text-emerald-600">{stats.available ?? 0}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-amber-400" />
                      <span className="text-xs text-gray-500">Consumed</span>
                      <span className="text-sm font-bold tabular-nums text-amber-600">{stats.consumed ?? 0}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-blue-400" />
                      <span className="text-xs text-gray-500">Issued</span>
                      <span className="text-sm font-bold tabular-nums text-blue-600">{stats.issued ?? 0}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} className="h-1" />

          {loadingMore && (
            <div className="space-y-3">
              {[1, 2].map(i => <div key={i} className="h-24 rounded-xl bg-gray-100 animate-pulse" />)}
            </div>
          )}

          {filmPagination && filmPage >= (filmPagination.pages ?? 1) && films.length > 0 && (
            <p className="text-center text-xs text-gray-400 py-2">
              All {filmPagination.total} film{filmPagination.total !== 1 ? "s" : ""} loaded
            </p>
          )}
        </>
      )}

      {/* Modals */}
      <AddFilmModal open={addOpen} onClose={() => setAddOpen(false)} onDone={() => done(() => setAddOpen(false))} />
      <AddFilmModal open={!!editTarget} film={editTarget} onClose={() => setEditTarget(null)} onDone={() => done(() => setEditTarget(null))} />
      <IssueModal open={!!issueTarget} film={issueTarget} onClose={() => setIssueTarget(null)} onDone={() => done(() => setIssueTarget(null))} />
      <AllocateModal open={!!allocateTarget} film={allocateTarget} mode="ALLOCATE" onClose={() => setAllocateTarget(null)} onDone={() => done(() => setAllocateTarget(null))} />
      <AllocateModal open={!!revokeTarget} film={revokeTarget} mode="REVOKE" onClose={() => setRevokeTarget(null)} onDone={() => done(() => setRevokeTarget(null))} />
      <ConsumeModal open={!!consumeTarget} film={consumeTarget} onClose={() => setConsumeTarget(null)} onDone={() => done(() => setConsumeTarget(null))} />
      <ReturnModal open={!!returnTarget} film={returnTarget} onClose={() => setReturnTarget(null)} onDone={() => done(() => setReturnTarget(null))} />
      <CloseFilmModal open={!!closeTarget} film={closeTarget} onClose={() => setCloseTarget(null)} onDone={() => done(() => setCloseTarget(null))} />
    </div>
  );
}
